import React from 'react';
import { Check, Minus } from '../../../bravosIcons';

/**
 * Trainer Library — shared multi-select filter dropdown.
 *
 * Mirrors `_bmad-output/design-references/library-ui-improvements/library-filter-sort-components.html`
 * (library-ui-improvements §4.2):
 * - First row is `Select all` — a regular menu-item with a checkbox, an
 *   uppercase meta label, and an indeterminate state when some-but-not-all
 *   options are selected. Shows `<selected> / <total>` while indeterminate.
 * - A thin rule separates the meta row from the option list.
 * - Each option row — custom checkbox + label + "would-match" count.
 * - Footer — `<N> selected` + a per-dropdown `Clear` link (resets only this filter).
 */
export type LibFilterOption = {
  id: string;
  label: string;
  /** Number of items that would match if this option is the only one selected,
   *  within the currently filtered + searched dataset minus this filter's contribution. */
  matchCount: number;
};

export type LibraryFilterPopoverProps = {
  options: LibFilterOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  onClose: () => void;
  /** Shown when the option set is empty (e.g. trainer has no equipment values yet). */
  emptyMessage?: string;
  /** Optional aria-label override for the popover region. */
  ariaLabel?: string;
};

export const LibraryFilterPopover: React.FC<LibraryFilterPopoverProps> = ({
  options,
  selected,
  onChange,
  onClose,
  emptyMessage,
  ariaLabel,
}) => {
  const total = options.length;
  const allSelected = total > 0 && options.every((opt) => selected.has(opt.id));
  const indeterminate = !allSelected && selected.size > 0;

  const handleSelectAllClick = () => {
    if (allSelected) {
      onChange(new Set());
    } else {
      onChange(new Set(options.map((opt) => opt.id)));
    }
  };

  const handleToggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const handleClear = () => onChange(new Set());

  return (
    <div
      className="lib-popover lib-filter-popover"
      role="dialog"
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      {options.length === 0 ? (
        <div className="lib-popover-row lib-popover-row--empty">
          {emptyMessage ?? 'Nothing to filter on yet.'}
        </div>
      ) : (
        <>
          <button
            type="button"
            className="lib-popover-row lib-popover-row--meta"
            onClick={handleSelectAllClick}
            aria-pressed={allSelected}
          >
            <span
              className={`lib-ms-check ${allSelected ? 'is-checked' : ''} ${indeterminate ? 'is-indeterminate' : ''}`}
              aria-hidden="true"
            >
              {allSelected && <Check />}
              {indeterminate && <Minus />}
            </span>
            <span className="lib-popover-row-label">Select all</span>
            {indeterminate && (
              <span className="lib-popover-row-count lib-tnum">{selected.size} / {total}</span>
            )}
          </button>

          <div className="lib-popover-rule" aria-hidden="true" />

          <div className="lib-popover-body" role="listbox" aria-multiselectable="true">
            {options.map((opt) => {
              const isChecked = selected.has(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  className="lib-popover-row"
                  onClick={() => handleToggle(opt.id)}
                  aria-pressed={isChecked}
                >
                  <span
                    className={`lib-ms-check ${isChecked ? 'is-checked' : ''}`}
                    aria-hidden="true"
                  >
                    {isChecked && <Check />}
                  </span>
                  <span className="lib-popover-row-label">{opt.label}</span>
                  <span className="lib-popover-row-count lib-tnum">{opt.matchCount}</span>
                </button>
              );
            })}
          </div>

          <div className="lib-popover-footer">
            <span className="lib-popover-footer-count">
              <strong className="lib-tnum">{selected.size}</strong> selected
            </span>
            <button
              type="button"
              className="lib-popover-footer-clear"
              onClick={handleClear}
              disabled={selected.size === 0}
            >
              Clear
            </button>
          </div>
        </>
      )}
    </div>
  );
};
